import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/synapse/Icon';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import type { Consultation, Patient } from '@/types/patients';
import { usePatient } from './usePatients';
import { useConsultation } from './useConsultations';
import { features } from '@/lib/featureFlags';
import { ageFromDob, formatShortDate, sexLabelKey } from './patientDisplay';
import { formatVisitRecency } from './visitRecency';
import { VisitSummaryPanel } from './VisitSummaryPanel';

/**
 * Route component for the read-only summary of a single (finalized) visit. It
 * shows only the data unique to that consultation, via `VisitSummaryPanel` —
 * the heavy multi-stage history-taking form is reserved for editing drafts
 * (`ConsultationPage`). The Visit Timeline routes finalized visits here and
 * drafts to the editor; if a draft is opened here directly we offer a
 * "Continue editing" link rather than read-only chrome.
 */
export function VisitSummaryPage() {
  const { patientId, consultationId } = useParams();
  if (!patientId || !consultationId) {
    return <VisitError messageKey="consult.notFound" />;
  }
  return <VisitSummaryLoaded patientId={patientId} consultationId={consultationId} />;
}

function VisitSummaryLoaded({
  patientId,
  consultationId,
}: {
  patientId: string;
  consultationId: string;
}) {
  const { t } = useTranslation();
  const patientQ = usePatient(patientId);
  const consultQ = useConsultation(consultationId);

  if (patientQ.isLoading || consultQ.isLoading) return <VisitSkeleton />;
  if (patientQ.isError || consultQ.isError || !patientQ.data || !consultQ.data) {
    const err = patientQ.error ?? consultQ.error;
    const msg = err instanceof ApiError ? translateApiError(err) : t('consult.couldNotLoad');
    return <VisitError message={msg} />;
  }

  return (
    <VisitSummaryShell
      patientId={patientId}
      patient={patientQ.data}
      consultation={consultQ.data}
    />
  );
}

function VisitSummaryShell({
  patientId,
  patient,
  consultation,
}: {
  patientId: string;
  patient: Patient;
  consultation: Consultation;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const age = ageFromDob(patient.dateOfBirth);
  const sex = t(sexLabelKey(patient.sex));
  const isDraft = consultation.status === 'draft';

  return (
    <div className="flex flex-col gap-[18px]">
      <header className="flex items-center gap-3.5 rounded-lg border border-line bg-paper px-5 py-3.5">
        <button
          type="button"
          onClick={() => navigate(`/patients/${patientId}`)}
          aria-label={t('consult.back')}
          className="flex h-[34px] w-[34px] items-center justify-center rounded-md border border-line-2 bg-paper text-ink hover:bg-paper-2"
        >
          <Icon name="back" size={17} />
        </button>
        <InitialsAvatar name={patient.fullName} size={38} />
        <div className="min-w-0">
          <div className="text-[15.5px] font-bold leading-tight tracking-[-0.02em]">
            {patient.fullName}
          </div>
          <div className="text-[12px] text-muted">
            {age != null && (
              <>
                {age} {t('patients.yrs')} ·{' '}
              </>
            )}
            {sex}
            {patient.nationalId && (
              <>
                {' · '}
                <span className="font-mono">
                  {t('patients.nicLabel')} {patient.nationalId}
                </span>
              </>
            )}
          </div>
        </div>
        <div
          className="ml-1 flex shrink-0 items-center gap-1.5 rounded-md border border-line-2 bg-paper-2 px-2.5 py-1 text-[11.5px] text-muted"
          title={t('patients.cardVisitTimeline')}
        >
          <Icon name="calendar" size={13} />
          <span className="font-medium text-ink">{formatShortDate(consultation.visitDate)}</span>
          <span>· {formatVisitRecency(consultation.visitDate, t)}</span>
        </div>
        <div className="flex-1" />
        {isDraft && features.doctorConsultEntry ? (
          <Link
            to={`/patients/${patientId}/consult/${consultation.id}`}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line-2 bg-paper px-3 text-[12.5px] font-semibold text-ink-2 hover:bg-paper-2"
          >
            <Icon name="edit" size={13} />
            {t('patients.visitSummary.continueEditing')}
          </Link>
        ) : (
          <Badge tone={isDraft ? 'warn' : 'success'} size="md">
            {isDraft ? t('patients.statusDraft') : t('patients.statusFinalized')}
          </Badge>
        )}
      </header>

      <VisitSummaryPanel
        payload={consultation.payload}
        sex={patient.sex}
        dispensed={consultation.dispensed}
        recordedFromPaper={consultation.clinicianUserId === null}
      />
    </div>
  );
}

// ---------- loading / error states ----------

function VisitSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <div className="h-16 animate-pulse rounded-lg bg-paper-2" />
      <div className="h-[60vh] animate-pulse rounded-lg bg-paper-2" />
    </div>
  );
}

function VisitError({ message, messageKey }: { message?: string; messageKey?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive-bg text-destructive">
        <Icon name="alert" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('consult.couldNotLoad')}</div>
      <p className="max-w-[320px] text-[12.5px] text-muted">
        {message ?? (messageKey ? t(messageKey) : '')}
      </p>
    </div>
  );
}
